import { useContext } from 'react'

import { toggleArray } from '@domain-group/javascript-utils'

import {
  useForm, useInterval, useRealtimeSubscription, useSetState,
} from '@domain-group/react-hooks'

import RealtimeContext from '@contexts/realtimeContext'

import useAgency from '@hooks/useAgency'
import useBrand from '@hooks/useBrand'
import useCampaign from '@hooks/useCampaign'
import useCurrentUser from '@hooks/useCurrentUser'

import imageDownloadPresetDefaultState, { requiredFields } from '@models/imageDownloadPreset'

import type { AgencyModel, CampaignModel, ImageDownloadPresetModel } from '@models/types'

const defaultPresets = [
  {
    isDefault: true,
    key: 'original',
    subtitle: 'Best Quality (Original)',
    title: 'Large',
    type: 'original',
  },
  {
    isDefault: true,
    key: 'small',
    subtitle: 'Web & Email (800x600)',
    title: 'Small',
    type: 'custom_800x600',
  },
]

export type ImageSizePreset = (typeof defaultPresets)[0]

const buildCustomPreset = (presetParams: ImageDownloadPresetModel) => {
  const {
    height, key, title, width,
  } = presetParams

  return {
    key,
    subtitle: `Custom (${width}x${height})`,
    title,
    type: `custom_${width}x${height}`,
  }
}

type UpdateAgencyPresetsParams = {
  agency: AgencyModel,
  customPreset: ImageSizePreset,
}

const buildAgencyPresets = (params: UpdateAgencyPresetsParams) => {
  const { agency, customPreset } = params

  // @ts-expect-error TS2339
  const existingCustomPresets = agency.options?.image_download_presets || []

  const updatedPresets = toggleArray(existingCustomPresets, customPreset, { deepCompare: true })

  return { options: { image_download_presets: updatedPresets } }
}

const defaultState = {
  downloadLink: '',
  downloadReady: false,
  processingImages: false,
  selectedPreset: defaultPresets[0],
  showCustomSizeForm: false,
  timer: 0,
  watermarkImagesEnabled: true,
}

type SetState = (state: Partial<typeof defaultState>) => void

type ImagesPayload = {
  payload: {
    links: { original_url: string },
  },
}

type ProcessingCompleteParams = {
  data: ImagesPayload,
  setState: SetState,
}

const processingComplete = (params: ProcessingCompleteParams) => {
  const { data, setState } = params

  const link = data.payload?.links.original_url || ''

  if (link) window.open(link, '_blank')

  setState({
    downloadLink: link,
    downloadReady: true,
    processingImages: false,
  })
}

type TimerParams = {
  setState: SetState,
  state: typeof defaultState,
}

// Count up so we can show different messages as time passes
const timerFn = (params: TimerParams) => {
  const { setState, state } = params
  const { processingImages, timer } = state

  if (processingImages){
    setState({ timer: timer + 1 })
  }
}

const useDownloadMedia = (initCampaign: CampaignModel) => {
  const [state, setState] = useSetState(defaultState)
  const {
    downloadLink, downloadReady, processingImages, selectedPreset, showCustomSizeForm, timer, watermarkImagesEnabled,
  } = state

  const { currentUser } = useCurrentUser()

  const {
    callbacks: { downloadImages },
    campaign,
    updating: updatingCampaign,
  } = useCampaign(initCampaign)

  const {
    agency,
    callbacks: { updateAgency },
    updating: updatingAgency,
  } = useAgency({ id: campaign.agency_id })

  const realtimeContext = useContext(RealtimeContext)
  // @ts-expect-error TS2339
  const { clientRef } = realtimeContext

  useRealtimeSubscription({
    apiKey: process.env.REACT_APP_ABLY_PUBLIC_KEY,
    callbacks: {
      success: (data: ImagesPayload) => processingComplete({ data, setState }),
    },
    channelName: 'campaigns',
    clientRef,
    eventName: `campaign.${campaign.id}.downloadPreparation.complete`,
  }, [campaign.id])

  useInterval(() => timerFn({ setState, state }), 1000, { enabled: processingImages })

  // @ts-expect-error
  const customPresets: ImageSizePreset[] = agency.options?.image_download_presets || []
  const presets = currentUser.id ? [...defaultPresets, ...customPresets] : defaultPresets

  const presetForm = useForm(imageDownloadPresetDefaultState, { entity: {}, requiredFields, validateOn: 'change' }, [
    customPresets.length,
  ])

  const brandPayload = useBrand({ id: agency?.brand_id })
  const { publicLinkFontColor, publicLinkTextAccentColour } = brandPayload

  const agencyWatermarkOnDownloadEnabled = agency.options?.watermark_images?.includes('download') || false

  return {
    agencyWatermarkOnDownloadEnabled,
    callbacks: {
      // @ts-expect-error TS2322
      buildAgencyPresets: (customPreset: ImageSizePreset) => buildAgencyPresets({ agency, customPreset }),
      buildCustomPreset: (presetParams: ImageDownloadPresetModel) => buildCustomPreset(presetParams),
      downloadImages,
      resetSelectedPreset: () => setState({ selectedPreset: defaultPresets[0] }),
      setState,
      updateAgency,
    },
    currentUser,
    downloadLink,
    downloadReady,
    presetForm,
    presets,
    processingImages,
    publicLinkFontColor,
    publicLinkTextAccentColour,
    selectedPreset,
    showCustomSizeForm,
    watermarkImagesEnabled,
    timer,
    updatingCampaign,
    updatingAgency,
  }
}

export type UseDownloadMediaPayload = ReturnType<typeof useDownloadMedia>

export default useDownloadMedia
